import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { Play, RefreshCw, AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Settings as SettingsIcon, Cookie, Upload, Youtube } from "lucide-react";

const REFRESH_SEC = 30;
const RECENT_LIMIT = 100;

export default function IgValidation() {
  const [config, setConfig] = useState({
    enabled: false,
    interval_min: 30,
    urls: [],
    notify_set: false,
  });
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [tab, setTab] = useState("validation");

  const load = useCallback(async () => {
    setError(null);
    try {
      const [settingsRes, runsRes] = await Promise.all([
        api.get("/admin/settings"),
        api.get("/admin/ig-validation/runs", { params: { limit: RECENT_LIMIT } }),
      ]);
      const byKey = Object.fromEntries(
        (settingsRes.data || []).map((s) => [s.key, s])
      );
      setConfig({
        enabled: (byKey.IG_VALIDATION_ENABLED?.effective_value || "false") === "true",
        interval_min: parseInt(byKey.IG_VALIDATION_INTERVAL_MIN?.effective_value || "30", 10),
        urls: (byKey.IG_VALIDATION_URLS?.effective_value || "")
          .split("\n")
          .map((s) => s.trim())
          .filter((s) => s && !s.startsWith("#")),
        notify_set: !!(byKey.IG_VALIDATION_NOTIFY_CHAT_ID?.effective_value || "").trim(),
      });
      // Endpoint MAY return non-array if auth glitched — coerce defensively.
      setRuns(Array.isArray(runsRes.data) ? runsRes.data : []);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_SEC * 1000);
    return () => clearInterval(t);
  }, [load]);

  const onRunNow = async () => {
    setRunning(true);
    setError(null);
    try {
      await api.post("/admin/ig-validation/run-now");
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Run failed");
    } finally {
      setRunning(false);
    }
  };

  const stats = useMemo(() => {
    if (!runs.length) return null;
    const total = runs.reduce((a, r) => a + r.total, 0);
    const fails = runs.reduce((a, r) => a + r.fail, 0);
    const allLatencies = runs.flatMap((r) => (r.results || []).map((x) => x.ms || 0));
    const avgMs = allLatencies.length
      ? Math.round(allLatencies.reduce((a, b) => a + b, 0) / allLatencies.length)
      : 0;
    const span =
      runs[runs.length - 1]?.started_at && runs[0]?.started_at
        ? hoursBetween(runs[runs.length - 1].started_at, runs[0].started_at)
        : 0;
    return {
      runCount: runs.length,
      total,
      fails,
      ok: total - fails,
      successRate: total > 0 ? ((100 * (total - fails)) / total).toFixed(1) : "—",
      avgMs,
      spanHours: span,
    };
  }, [runs]);

  const toggleExpanded = (id) => {
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-6" data-testid="ig-validation-page">
      {/* Header + Run Now */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display tracking-tight">IG Cookies Validation</h1>
          <p className="text-sm text-muted-foreground mt-1">
            24h survival monitor for the shared Instagram cookie jar. Probes a
            configured set of Reels every <b>{config.interval_min} min</b>; pings
            you in Telegram on first failure.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={load}
            className="inline-flex items-center gap-2 px-3 py-2 text-xs border border-border hover:bg-secondary/40"
            data-testid="ig-refresh"
            title="Refresh"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
          <button
            onClick={onRunNow}
            disabled={running || !config.enabled || !config.urls.length}
            className="inline-flex items-center gap-2 px-3 py-2 text-xs bg-foreground text-background hover:opacity-90 disabled:opacity-30"
            data-testid="ig-run-now"
            title="Force a probe right now"
          >
            <Play className="w-3.5 h-3.5" />
            {running ? "Running…" : "Run Now"}
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 border border-destructive/40 text-destructive text-xs flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div>
            <div className="font-mono">{error}</div>
          </div>
        </div>
      )}

      {/* Tab strip — Validation (default) | Cookies */}
      <div className="flex border-b border-border">
        <TabButton
          active={tab === "validation"}
          onClick={() => setTab("validation")}
          icon={<Play className="w-3.5 h-3.5" />}
          testId="ig-tab-validation"
        >
          Validation
        </TabButton>
        <TabButton
          active={tab === "cookies"}
          onClick={() => setTab("cookies")}
          icon={<Cookie className="w-3.5 h-3.5" />}
          testId="ig-tab-cookies"
        >
          Cookies
        </TabButton>
        <TabButton
          active={tab === "youtube"}
          onClick={() => setTab("youtube")}
          icon={<Youtube className="w-3.5 h-3.5" />}
          testId="yt-tab-cookies"
        >
          YouTube
        </TabButton>
      </div>

      {tab === "cookies" && <CookiesTab />}

      {tab === "youtube" && <YouTubeTab />}

      {tab === "validation" && <>
      {/* Status / Config card */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <StatusCard
          label="Validation"
          value={config.enabled ? "ENABLED" : "DISABLED"}
          good={config.enabled}
          warn={!config.enabled}
        />
        <StatusCard
          label="URLs configured"
          value={`${config.urls.length}`}
          good={config.urls.length > 0}
          warn={config.urls.length === 0}
        />
        <StatusCard
          label="Telegram alerts"
          value={config.notify_set ? "ON" : "OFF"}
          good={config.notify_set}
          warn={!config.notify_set}
        />
        <StatusCard
          label="Interval"
          value={`${config.interval_min} min`}
          good
        />
      </div>

      {/* Configured URLs */}
      {config.urls.length > 0 ? (
        <div className="border border-border">
          <div className="px-3 py-2 border-b border-border flex items-center justify-between bg-secondary/20">
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
              URLs being monitored ({config.urls.length})
            </div>
            <Link
              to="/admin/settings"
              className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            >
              <SettingsIcon className="w-3 h-3" /> edit
            </Link>
          </div>
          <ul className="divide-y divide-border">
            {config.urls.map((u, i) => (
              <li key={i} className="px-3 py-2 text-xs font-mono truncate" title={u}>
                <span className="text-muted-foreground">#{i + 1}</span>{" "}
                <a href={u} target="_blank" rel="noreferrer" className="hover:underline">
                  {u}
                </a>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="border border-amber-500/40 bg-amber-500/5 p-4 text-sm">
          <div className="font-medium mb-1">⚠️ No URLs configured</div>
          <div className="text-muted-foreground text-xs">
            Set <code className="font-mono text-foreground">IG_VALIDATION_URLS</code>{" "}
            in <Link to="/admin/settings" className="underline">Settings</Link> —
            paste 3-5 public Instagram Reel URLs, one per line.
          </div>
        </div>
      )}

      {/* Stats summary */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <SummaryStat label="Runs" value={stats.runCount} sub={`${stats.spanHours}h span`} />
          <SummaryStat label="Probes total" value={stats.total} />
          <SummaryStat label="Successes" value={stats.ok} good />
          <SummaryStat label="Failures" value={stats.fails} bad={stats.fails > 0} />
          <SummaryStat
            label="Success rate"
            value={`${stats.successRate}%`}
            good={stats.fails === 0}
            bad={parseFloat(stats.successRate) < 95}
          />
        </div>
      )}

      {/* Runs table */}
      <div className="border border-border">
        <div className="px-3 py-2 border-b border-border bg-secondary/20 flex items-center justify-between">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
            Recent runs (newest first, auto-refresh every {REFRESH_SEC}s)
          </div>
          <div className="text-[10px] font-mono text-muted-foreground">
            {loading ? "loading…" : `${runs.length} runs`}
          </div>
        </div>
        {!runs.length && !loading && (
          <div className="p-6 text-center text-xs text-muted-foreground">
            No runs yet. Click <b>Run Now</b> to start, or wait for the next
            cron tick.
          </div>
        )}
        {runs.length > 0 && (
          <table className="w-full text-xs">
            <thead className="border-b border-border bg-secondary/10">
              <tr>
                <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">When</th>
                <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Score</th>
                <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Avg latency</th>
                <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">First failure</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => {
                const isOpen = expanded.has(r._id);
                const avgMs = (r.results || []).length
                  ? Math.round(
                      r.results.reduce((a, b) => a + (b.ms || 0), 0) / r.results.length,
                    )
                  : 0;
                const firstFail = (r.results || []).find((x) => !x.ok);
                return (
                  <React.Fragment key={r._id}>
                    <tr
                      className="border-b border-border hover:bg-secondary/20 cursor-pointer"
                      onClick={() => toggleExpanded(r._id)}
                      data-testid={`ig-run-row-${r._id}`}
                    >
                      <td className="px-3 py-2 font-mono whitespace-nowrap">
                        {formatWhen(r.started_at)}
                      </td>
                      <td className="px-3 py-2 font-mono">
                        <ScoreBadge ok={r.ok} total={r.total} />
                      </td>
                      <td className="px-3 py-2 font-mono text-muted-foreground">
                        {avgMs}ms
                      </td>
                      <td className="px-3 py-2 font-mono text-muted-foreground truncate max-w-md">
                        {firstFail ? (
                          <span className="text-destructive" title={firstFail.error}>
                            {firstFail.error?.slice(0, 100)}
                          </span>
                        ) : (
                          <span className="text-emerald-500">all ok</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-muted-foreground">
                        {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-secondary/10 border-b border-border">
                        <td colSpan={5} className="p-0">
                          <div className="px-3 py-2">
                            <table className="w-full text-[11px] font-mono">
                              <tbody>
                                {(r.results || []).map((res, i) => (
                                  <tr key={i} className="border-t border-border/50 first:border-t-0">
                                    <td className="py-1.5 pr-2 w-5">
                                      {res.ok ? (
                                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                                      ) : (
                                        <AlertTriangle className="w-3.5 h-3.5 text-destructive" />
                                      )}
                                    </td>
                                    <td className="py-1.5 pr-2 truncate max-w-xs" title={res.url}>
                                      {res.url?.slice(-50)}
                                    </td>
                                    <td className="py-1.5 pr-2 text-muted-foreground">
                                      {res.ms}ms
                                    </td>
                                    <td className="py-1.5 pr-2">
                                      {res.ok ? (
                                        <span className="text-muted-foreground">
                                          {res.title?.slice(0, 50) || "—"}
                                          {res.duration_sec != null ? ` (${res.duration_sec}s)` : ""}
                                        </span>
                                      ) : (
                                        <span className="text-destructive">{res.error}</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      </>}
    </div>
  );
}

function TabButton({ active, onClick, icon, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-2 px-4 py-2 text-xs font-mono uppercase tracking-[0.18em] border-b-2 transition-colors ${
        active
          ? "border-foreground text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground"
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

function CookiesTab() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [okMsg, setOkMsg] = useState(null);
  const [smokeResult, setSmokeResult] = useState(null);
  const [smokeRunning, setSmokeRunning] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.get("/admin/ig-cookies/status");
      setStatus(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load status");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runSmokeTest = useCallback(async () => {
    setSmokeRunning(true);
    setSmokeResult(null);
    try {
      const { data } = await api.post("/admin/ig-cookies/smoke-test");
      setSmokeResult(data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Smoke test failed";
      // A missing IG_VALIDATION_URLS config is our problem, not Instagram's —
      // don't paint it as "Instagram rejected".
      setSmokeResult({
        ok: false,
        misconfigured: /no test urls?/i.test(detail),
        error: detail,
      });
    } finally {
      setSmokeRunning(false);
    }
  }, []);

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setError(null);
    setOkMsg(null);
    setSmokeResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/ig-cookies/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setStatus(data.status);
      setOkMsg(
        `Uploaded ${file.name} — ${data.cookie_count ?? data.status.cookie_count} cookies, ` +
        `${data.size_bytes ?? data.status.size_bytes} bytes, encrypted in DB` +
        (data.mirrored_to_file ? " (also mirrored to the host file)." : "."),
      );
      // Auto-trigger smoke test — verify Instagram actually accepts these.
      runSmokeTest();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeJar = async () => {
    setUploading(true);
    setError(null);
    setOkMsg(null);
    try {
      await api.delete("/admin/ig-cookies");
      setOkMsg("Shared Instagram jar removed.");
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Delete failed");
    } finally {
      setUploading(false);
    }
  };

  const onFileChange = (e) => onUpload(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault();
    onUpload(e.dataTransfer.files?.[0]);
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground">Loading cookies status…</div>;
  }

  const ageDays = status?.age_days ?? 0;
  const threshold = status?.warn_threshold_days ?? 20;
  const pct = Math.min(100, Math.round((ageDays / threshold) * 100));
  const isStale = status?.stale;
  const isExpired = status?.sessionid_expired;

  return (
    <div className="space-y-6" data-testid="ig-cookies-tab">
      {error && (
        <div className="p-3 border border-destructive/40 text-destructive text-xs flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div className="font-mono">{error}</div>
        </div>
      )}
      {okMsg && (
        <div className="p-3 border border-emerald-500/40 text-emerald-500 text-xs flex items-start gap-2">
          <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div className="font-mono">{okMsg}</div>
        </div>
      )}

      {/* Status cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <StatusCard
          label="Jar"
          value={status?.stored_in_db ? "IN DATABASE" : status?.exists ? "LEGACY FILE" : "MISSING"}
          good={status?.stored_in_db || status?.exists}
          warn={!status?.stored_in_db && !status?.exists}
        />
        <StatusCard
          label="sessionid"
          value={status?.stored_in_db ? "PRESENT" : status?.has_sessionid ? (isExpired ? "EXPIRED" : "PRESENT") : "MISSING"}
          good={status?.stored_in_db || (status?.has_sessionid && !isExpired)}
          warn={!status?.stored_in_db && (!status?.has_sessionid || isExpired)}
        />
        <StatusCard
          label="Cookies"
          value={`${status?.db_jar?.cookie_count ?? status?.cookie_count ?? 0}`}
          good={(status?.db_jar?.cookie_count ?? status?.cookie_count ?? 0) > 0}
          warn={(status?.db_jar?.cookie_count ?? status?.cookie_count ?? 0) === 0}
        />
        <StatusCard
          label="Size"
          value={
            status?.db_jar?.size_bytes
              ? `${status.db_jar.size_bytes} B`
              : status?.size ? `${status.size} B` : "—"
          }
          good={(status?.db_jar?.size_bytes ?? status?.size ?? 0) > 0}
        />
      </div>

      {status?.stored_in_db && (
        <div
          className="border border-border p-3 text-xs flex items-center justify-between gap-3"
          data-testid="ig-db-jar-info"
        >
          <span className="opacity-60">
            Stored encrypted in the database
            {status.db_jar?.uploaded_by ? ` · by ${status.db_jar.uploaded_by}` : ""}
            {status.db_jar?.uploaded_at ? ` · ${status.db_jar.uploaded_at}` : ""}
          </span>
          <button
            onClick={removeJar}
            disabled={uploading}
            data-testid="ig-delete-btn"
            className="rounded-md border border-red-500/40 px-2.5 py-1 text-[11px]
                       text-red-300 hover:bg-red-500/10 disabled:opacity-50 transition-colors"
          >
            Remove jar
          </button>
        </div>
      )}

      {/* Age progress bar — only meaningful for the legacy host file; a
          DB-stored jar reports its own upload timestamp instead. */}
      {status?.exists && !status?.stored_in_db && (
        <div className="border border-border p-4 space-y-2" data-testid="ig-cookies-age">
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
              Age
            </div>
            <div className={`text-xs font-mono ${isStale ? "text-amber-500" : "text-foreground"}`}>
              {ageDays} / {threshold} days
            </div>
          </div>
          <div className="h-2 bg-secondary/40 overflow-hidden">
            <div
              className={`h-full transition-all ${
                pct >= 100 ? "bg-destructive" : pct >= 80 ? "bg-amber-500" : "bg-emerald-500"
              }`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="text-xs text-muted-foreground font-mono">
            Last refreshed: {status.mtime_iso || "—"}
            {status.sessionid_expires_iso && (
              <> · sessionid expires: {status.sessionid_expires_iso}</>
            )}
          </div>
          {isStale && (
            <div className="text-xs text-amber-500 font-mono pt-1">
              ⚠ Past warning threshold. Refresh before sessionid dies entirely.
            </div>
          )}
        </div>
      )}

      {/* Smoke test — live verification against Instagram */}
      {status?.exists && (
        <div className="border border-border p-4 space-y-3" data-testid="ig-cookies-smoke">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
                Live test
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Runs a single yt-dlp probe against a configured Reel — confirms Instagram actually accepts these cookies (not just that the format is valid).
              </div>
            </div>
            <button
              onClick={runSmokeTest}
              disabled={smokeRunning}
              className="shrink-0 inline-flex items-center gap-2 px-3 py-2 text-xs border border-border hover:bg-secondary/40 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="ig-cookies-smoke-btn"
            >
              <Play className="w-3.5 h-3.5" />
              {smokeRunning ? "Testing…" : "Smoke test now"}
            </button>
          </div>
          {smokeResult && (
            <div
              className={`p-3 text-xs font-mono border ${
                smokeResult.ok
                  ? "border-emerald-600/40 text-emerald-700 dark:text-emerald-400"
                  : smokeResult.misconfigured
                  ? "border-amber-500/40 text-amber-600 dark:text-amber-400"
                  : "border-destructive/40 text-destructive"
              }`}
              data-testid="ig-cookies-smoke-result"
            >
              {smokeResult.ok ? (
                <>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                    <span>cookies accepted by Instagram · {smokeResult.latency_ms}ms</span>
                  </div>
                  {smokeResult.title && (
                    <div className="mt-1 pl-5 text-muted-foreground truncate">
                      title: {smokeResult.title}
                      {smokeResult.duration_sec && <> · duration {smokeResult.duration_sec.toFixed(1)}s</>}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    <span>
                      {smokeResult.misconfigured
                        ? "Smoke test not configured"
                        : `Instagram rejected: ${smokeResult.error_code || "error"} (${smokeResult.latency_ms ?? "?"}ms)`}
                    </span>
                  </div>
                  {smokeResult.error && (
                    <div className="mt-1 pl-5 text-muted-foreground break-words">
                      {smokeResult.error}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Upload dropzone */}
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className="border-2 border-dashed border-border p-8 text-center hover:bg-secondary/20 transition-colors"
        data-testid="ig-cookies-dropzone"
      >
        <Upload className="w-8 h-8 mx-auto text-muted-foreground" />
        <div className="mt-3 text-sm">
          Drop new <code className="font-mono text-xs">cookies.txt</code> here or
        </div>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="mt-3 inline-flex items-center gap-2 px-4 py-2 text-xs bg-foreground text-background hover:opacity-90 disabled:opacity-30"
          data-testid="ig-cookies-pick"
        >
          {uploading ? "Uploading…" : "Choose File"}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".txt,text/plain"
          onChange={onFileChange}
          className="hidden"
          data-testid="ig-cookies-input"
        />
        <div className="mt-4 text-[10px] text-muted-foreground font-mono uppercase tracking-[0.14em]">
          Netscape format · must contain sessionid · &lt;1 MB
        </div>
      </div>

      {/* How to export */}
      <details className="border border-border p-3 text-xs">
        <summary className="cursor-pointer font-mono uppercase tracking-[0.14em] text-muted-foreground">
          How to export fresh cookies
        </summary>
        <ol className="mt-3 space-y-2 pl-4 list-decimal text-muted-foreground">
          <li>Open Chrome / Firefox and log in to <code>instagram.com</code> with a service account.</li>
          <li>Install the <a href="https://chromewebstore.google.com/detail/get-cookiestxt-locally/cclelndahbckbenkjhflpdbgdldlbecc" target="_blank" rel="noopener noreferrer" className="underline">Get cookies.txt LOCALLY</a> extension (Chrome) or any cookies.txt exporter for Firefox.</li>
          <li>On instagram.com, click the extension → Export → download.</li>
          <li>Drop the file here. yt-dlp picks it up immediately — no container restart needed.</li>
        </ol>
      </details>

      {/* Path debug */}
      {status?.path && (
        <div className="text-[10px] font-mono text-muted-foreground">
          Server path: {status.path}
        </div>
      )}
    </div>
  );
}

function StatusCard({ label, value, good, warn }) {
  const tone = good
    ? "border-emerald-500/40 text-emerald-500"
    : warn
    ? "border-amber-500/40 text-amber-500"
    : "border-border text-foreground";
  return (
    <div className={`border ${tone} p-3`}>
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 font-mono text-sm font-medium">{value}</div>
    </div>
  );
}

function SummaryStat({ label, value, sub, good, bad }) {
  const tone = bad ? "text-destructive" : good ? "text-emerald-500" : "text-foreground";
  return (
    <div className="border border-border p-3">
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
        {label}
      </div>
      <div className={`mt-1 font-mono text-xl font-medium ${tone}`}>{value}</div>
      {sub && <div className="text-[10px] font-mono text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function ScoreBadge({ ok, total }) {
  const fail = total - ok;
  if (fail === 0) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-emerald-500/40 text-emerald-500 text-[10px]">
        {ok}/{total}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-destructive/40 text-destructive text-[10px]">
      {ok}/{total}
    </span>
  );
}

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = Date.now();
    const ageSec = (now - d.getTime()) / 1000;
    if (ageSec < 60) return "just now";
    if (ageSec < 3600) return `${Math.round(ageSec / 60)}m ago`;
    if (ageSec < 86400) return `${Math.round(ageSec / 3600)}h ago`;
    return d.toISOString().slice(5, 16).replace("T", " ");
  } catch {
    return iso.slice(0, 16);
  }
}

function hoursBetween(aIso, bIso) {
  try {
    const a = new Date(aIso).getTime();
    const b = new Date(bIso).getTime();
    return Math.max(0, Math.round((b - a) / 3600000));
  } catch {
    return 0;
  }
}


// ── YouTube tab (iter63) ────────────────────────────────────────────────
// Shared cookie jar upload (no more scp'ing files to the box) plus the
// player-client probe: YouTube keeps moving clients onto SABR-only
// delivery, so the winner is discovered instead of hand-guessed.
function YouTubeTab() {
  const [status, setStatus] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [probe, setProbe] = useState(null);
  const [probing, setProbing] = useState(false);
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/youtube-cookies/status");
      setStatus(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const pickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setText(await f.text());
  };

  const save = async () => {
    setBusy(true); setError(null); setMsg(null);
    try {
      const { data } = await api.post("/admin/youtube-cookies", { cookies_text: text });
      setMsg(
        `Saved — ${data.cookie_count} cookies, ${data.size_bytes} bytes, encrypted in DB` +
        (data.mirrored_to_file ? " (also mirrored to the host file)." : "."),
      );
      setText("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Upload failed");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true); setError(null); setMsg(null);
    try {
      await api.delete("/admin/youtube-cookies");
      setMsg("Shared YouTube jar removed.");
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Delete failed");
    } finally { setBusy(false); }
  };

  const runProbe = async () => {
    setProbing(true); setError(null); setMsg(null); setProbe(null);
    try {
      const { data } = await api.post("/admin/youtube/probe-client");
      setProbe(data);
      if (data.ok) setMsg(`Selected player client: ${data.selected}`);
      else setError("No candidate client worked — try uploading cookies.");
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Probe failed");
    } finally { setProbing(false); }
  };

  return (
    <div className="space-y-4" data-testid="yt-cookies-tab">
      {error && (
        <div className="rounded-lg border border-red-500/40 bg-red-500/5 p-3 text-sm text-red-300"
             data-testid="yt-error">{error}</div>
      )}
      {msg && (
        <div className="rounded-lg border border-emerald-600/40 bg-emerald-500/5 p-3 text-sm text-emerald-700 dark:text-emerald-300"
             data-testid="yt-ok">{msg}</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="rounded-lg border border-border p-3" data-testid="yt-jar-status">
          <div className="text-xs uppercase tracking-wide opacity-60">Shared cookie jar</div>
          <div className="mt-1 font-mono text-sm">
            {status?.stored_in_db
              ? `${status.db_jar?.cookie_count ?? 0} cookies · ${status.db_jar?.size_bytes ?? 0} bytes`
              : status?.exists
              ? `legacy file · ${status.size_bytes ?? 0} bytes`
              : "missing (upload below)"}
          </div>
          <div className="mt-1 text-[11px] opacity-50">
            {status?.stored_in_db
              ? `encrypted in database${status.db_jar?.uploaded_by ? ` · by ${status.db_jar.uploaded_by}` : ""}`
              : status?.exists
              ? "served from the host file — re-upload here to move it into the database"
              : "stored encrypted in the database — no host file needed"}
          </div>
          {status?.exists && (
            <div className="mt-1 text-[11px] opacity-40 break-all">
              legacy file mirror: {status.path} · {status.size_bytes} bytes
            </div>
          )}
          {status?.stored_in_db && (
            <button
              onClick={remove}
              disabled={busy}
              data-testid="yt-delete-btn"
              className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-red-500/40
                         px-2.5 py-1 text-[11px] text-red-300 hover:bg-red-500/10
                         disabled:opacity-50 transition-colors"
            >
              Remove jar
            </button>
          )}
        </div>
        <div className="rounded-lg border border-border p-3" data-testid="yt-client-status">
          <div className="text-xs uppercase tracking-wide opacity-60">Player client</div>
          <div className="mt-1 font-mono text-sm">{status?.player_client || "default (yt-dlp)"}</div>
          <div className="mt-1 text-[11px] opacity-50">
            auto-probe {status?.autoprobe ? "on" : "off"}
          </div>
        </div>
        <div className="rounded-lg border border-border p-3">
          <div className="text-xs uppercase tracking-wide opacity-60">Candidates</div>
          <div className="mt-1 font-mono text-[11px] break-all">{status?.candidates}</div>
          <button
            onClick={runProbe}
            disabled={probing}
            data-testid="yt-probe-btn"
            className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-primary/10 px-2.5 py-1.5 text-xs
                       hover:bg-primary/20 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${probing ? "animate-spin" : ""}`} />
            {probing ? "Probing…" : "Probe now"}
          </button>
        </div>
      </div>

      {probe?.tried?.length > 0 && (
        <div className="rounded-lg border border-border p-3" data-testid="yt-probe-result">
          <div className="text-xs uppercase tracking-wide opacity-60 mb-2">Probe result</div>
          <ul className="space-y-1 text-xs font-mono">
            {probe.tried.map((t) => (
              <li key={t.client} className={t.ok ? "text-emerald-700 dark:text-emerald-300" : "opacity-70"}>
                {t.ok ? "✓" : "✕"} {t.client} · {t.ms}ms{t.error ? ` · ${t.error}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="rounded-lg border border-border p-3 space-y-2">
        <div className="text-xs uppercase tracking-wide opacity-60">
          Upload cookies.txt (Netscape format, youtube.com)
        </div>
        <input
          ref={fileRef}
          type="file"
          accept=".txt"
          onChange={pickFile}
          data-testid="yt-file-input"
          className="block w-full text-xs"
        />
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={6}
          spellCheck={false}
          placeholder={"# Netscape HTTP Cookie File\n.youtube.com\tTRUE\t/\tTRUE\t…"}
          data-testid="yt-cookies-textarea"
          className="w-full rounded-md bg-background border border-border p-2 font-mono text-[11px]"
        />
        <button
          onClick={save}
          disabled={busy || !text.trim()}
          data-testid="yt-save-btn"
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs
                     text-primary-foreground hover:opacity-90 disabled:opacity-50 transition-opacity"
        >
          <Upload className="w-3.5 h-3.5" />
          {busy ? "Saving…" : "Save cookies"}
        </button>
      </div>
    </div>
  );
}
